import { isFile, isDefined, isUndefined, isString } from './utilities'
import mime from 'mime-types'
import Request, { POST } from '@/plugins/sdk/Request'
import Session from '@/plugins/session'

export default class FileManager {
  constructor (Vue, overrides) {
    const instance = this

    const vm = new Vue()

    Vue.file = instance

    Object.defineProperties(Vue.prototype, {
      $file: {
        get () {
          return instance
        }
      }
    })

    Object.defineProperties(this, {
      $http: {
        get () {
          return Vue.prototype.$http
        }
      },
      $sdk: {
        get () {
          return Vue.prototype.$sdk
        }
      },
      __vm: {
        get () {
          return vm
        }
      }
    })
  }

  /**
   * Returns a link to the file.
   *
   * @param      {String}   id      The file identifier
   */
  link (id, direct) {
    if (isDefined(id)) {
      if (isUndefined(direct)) {
        direct = false
      }

      let path

      if (direct) {
        path = `${process.env.VUE_APP_API_HOSTNAME}api/v3/file`
      } else {
        path = `${process.env.VUE_APP_URI}/download`
      }

      return `${path}/${id}`
    }
  }

  /**
   * Removes a file.
   *
   * @param      {String}   id      The file identifier
   */
  remove (id) {
    return new Promise((resolve, reject) => {
      this.$http({
        url: `/file/${id}`,
        method: 'DELETE'
      })
        .then(() => {
          return resolve()
        })
        .catch((err) => {
          return reject(err)
        })
    })
  }

  /**
   * Downloads a file.
   *
   * @param      {String}   id      The file identifier
   * @param      {Boolean}  inline  If the download should be rendered inline
   *                                vs as an attachment.
   */
  async download (id, inline) {
    if (isDefined(id)) {
      let query = `?disposition=${inline ? 'inline' : 'attachment'}`

      // if (this.__vm.$auth.authenticated()) {
      query += `&session=${await Session.token}`
      // }

      window.open(this.link(id, true) + query)
    }
  }

  displayImage (id) {
    if (isDefined(id)) {
      let query = `?disposition=inline`

      if (this.__vm.$auth.authenticated()) {
        query += `&session=${this.__vm.$auth.token}`
      }
      return this.link(id, true) + query
    }
  }

  /**
   * Uploads a file.
   *
   * @param      {<type>}   contents    The file contents
   * @param      {String}   repository  The file repository
   * @param      {String}   name        The file name
   * @param      {Boolean}  isShared    If the file is publicly shared
   * @param      {Boolean}  isPublic    If the file is publicly shared
   * @return     {Promise}
   */
  async upload (contents, repository, name, isShared) {
    if (isDefined(contents)) {
      // let file = new this.__vm.$sdk.file()
      // file.file = isFile(contents) ? contents : new File([new Blob([contents])], name || 'New File')
      // file.name = isDefined(name) ? name : undefined
      // file.repository = isDefined(repository) ? repository : undefined
      // file.is_shared = isDefined(isShared) ? !!isShared : false
      let data = new FormData()

      data.append(
        'file',
        isFile(contents)
          ? contents
          : new File([new Blob([contents])], name || 'New File')
      )

      if (isDefined(repository)) {
        data.append('repository', repository)
      }

      if (isDefined(name)) {
        data.append('name', name)
      }

      if (isDefined(isShared)) {
        data.append('is_shared', !!isShared)
      }

      try {
        const request = Object.assign(new Request(), {
          _pathname: 'file',
          method: POST,
          _body: data
        })
        let response = await fetch(await request.build())
        if (response.ok) {
          response = await response.json()
          return response
        } else {
          return new Error('An error occurred while uploading file')
        }
      } catch {
        return new Error('An error occurred while uploading file')
      }
    } else {
      return new Error('Contents of the file is required')
    }
  }

  setAttachment (fileId, accountId) {
    return new Promise((resolve, reject) => {
      if (!fileId || !accountId) return reject(new Error('file ID and accountId are required!'))
      const file = new this.__vm.$sdk.file()

      file.id = fileId
      file.is_shared = true

      this.__vm.$sdk.attachment.create({
        resource: 'account',
        resource_uuid: accountId,
        file
      })
        .then(res => { resolve(res) })
        .catch(err => { reject(err) })
    })
  }

  /**
   * Sets Attachment for non-account resources.
   *
   * @param      {String}   fileId    The file
   * @param      {String}   uuid        The resource UUID
   * @param      {String}   resource    The resource type
   * @param      {Boolean}  isPublic    If the file is publicly shared
   * @return     {Promise}
   */
  setGenericAttachment (fileId, uuid, resource, isPublic = false) {
    return new Promise((resolve, reject) => {
      if (!fileId || !uuid || !resource) return reject(new Error('file ID and accountId are required!'))
      const file = new this.__vm.$sdk.file()

      file.id = fileId
      file.is_shared = true
      file.is_public = isPublic

      this.__vm.$sdk.attachment.create({
        resource: resource,
        resource_uuid: uuid,
        file
      })
        .then(res => { resolve(res) })
        .catch(err => { reject(err) })
    })
  }

  /**
   * Returns an icon name for the passed file extension or mime-type.
   *
   * @param      {String}  extension    A file extension or mime-type
   * @return     {String}
   */
  icon (extension) {
    // If passed extension is possibly a mime-type, attempt to resolve the mime-type its default extension. Fallback
    // to passed extension if default mime-type extension cannot be determined.
    if (isString(extension) && extension.includes('/')) {
      extension = mime.extension(extension) || extension
    }

    let icon

    switch (extension) {
      case 'pdf':
        icon = 'file-pdf'
        break
      case 'csv':
        icon = 'file-delimited'
        break
      case 'xml':
        icon = 'file-xml'
        break
      case 'xls':
      case 'xlsx':
      case 'ods':
      case 'fods':
        icon = 'file-excel'
        break
      case 'doc':
      case 'docx':
      case 'odt':
      case 'fodt':
        icon = 'file-word'
        break
      case 'ppt':
      case 'pptx':
      case 'odp':
      case 'fodp':
        icon = 'file-powerpoint'
        break
      case 'tif':
      case 'tiff':
      case 'gif':
      case 'jpeg':
      case 'jpg':
      case 'jif':
      case 'jfif':
      case 'jp2':
      case 'jpx':
      case 'j2k':
      case 'j2c':
      case 'png':
      case 'bmp':
      case 'psd':
        icon = 'file-image'
        break
      default:
        icon = 'file'
    }

    return icon
  }
}
